import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, vModelText as _vModelText, normalizeClass as _normalizeClass, withDirectives as _withDirectives, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createBlock as _createBlock, Fragment as _Fragment, withModifiers as _withModifiers, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "space-y-6" }
const _hoisted_2 = { class: "form-group" }
const _hoisted_3 = {
  key: 0,
  class: "mt-2 text-sm text-red-600 font-medium"
}
const _hoisted_4 = { class: "form-group" }
const _hoisted_5 = {
  key: 0,
  class: "mt-2 text-sm text-red-600 font-medium"
}
const _hoisted_6 = { class: "form-group" }
const _hoisted_7 = { class: "flex flex-col sm:flex-row sm:items-center sm:justify-between bg-gray-100 dark:bg-gray-700 rounded-lg px-4 py-3 border border-gray-200 dark:border-gray-600 gap-3" }
const _hoisted_8 = {
  key: 0,
  class: "form-group"
}
const _hoisted_9 = { class: "flex items-center justify-between p-3 bg-gray-50 rounded-lg" }
const _hoisted_10 = { class: "flex items-center min-w-0 flex-1" }
const _hoisted_11 = { class: "min-w-0 flex-1" }
const _hoisted_12 = { class: "text-sm font-medium text-gray-900 truncate" }
const _hoisted_13 = { class: "text-xs text-gray-500" }
const _hoisted_14 = ["disabled"]
const _hoisted_15 = {
  key: 1,
  class: "text-gray-400 dark:text-gray-500 ml-2 flex-shrink-0",
  title: "Download restricted to administrators"
}
const _hoisted_16 = {
  key: 0,
  class: "mt-2 text-sm text-amber-600 dark:text-amber-400 flex items-center"
}
const _hoisted_17 = { class: "form-group" }
const _hoisted_18 = { class: "block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2" }
const _hoisted_19 = { class: "space-y-4" }
const _hoisted_20 = {
  key: 0,
  class: "text-center"
}
const _hoisted_21 = {
  key: 1,
  class: "flex items-center justify-between p-3 bg-gray-50 rounded-lg"
}
const _hoisted_22 = { class: "flex items-center min-w-0 flex-1" }
const _hoisted_23 = { class: "min-w-0 flex-1" }
const _hoisted_24 = { class: "text-sm font-medium text-gray-900 truncate" }
const _hoisted_25 = { class: "text-xs text-gray-500" }
const _hoisted_26 = {
  key: 0,
  class: "text-sm text-red-600 font-medium"
}
const _hoisted_27 = {
  key: 1,
  class: "mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4"
}
const _hoisted_28 = { class: "mt-3 space-y-2" }
const _hoisted_29 = {
  key: 0,
  class: "flex items-center text-sm text-red-600 dark:text-red-400 font-medium"
}
const _hoisted_30 = {
  key: 1,
  class: "flex items-center text-sm text-yellow-700 dark:text-yellow-400 font-medium"
}
const _hoisted_31 = {
  key: 2,
  class: "flex items-center text-sm text-green-600 dark:text-green-400 font-medium"
}
const _hoisted_32 = {
  key: 3,
  class: "flex items-center text-sm text-gray-600 dark:text-gray-400 font-medium"
}

import { ref, reactive, computed, watch, nextTick } from 'vue'
import {
  Edit,
  Upload,
  FileText,
  Download,
  X,
  Loader2,
  XCircle,
  CheckCircle,
  Info
} from 'lucide-vue-next'
import BaseModal from '@/components/common/BaseModal.vue'
import BaseButton from '@/components/common/BaseButton.vue'
import { ModulesService, type Module } from '@/services/modules'
import { useAuthStore } from '@/stores/auth'
import { useToastService } from '@/services/toast'
import { getErrorMessage } from '@/utils/errorHandler'

interface Props {
  isVisible: boolean
  module?: Module | null
}


export default /*@__PURE__*/_defineComponent({
  __name: 'EditModule',
  props: {
    isVisible: { type: Boolean, default: false },
    module: { default: null }
  },
  emits: ["close", "success"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

// Reactive data
const formData = reactive({
  name: '',
  description: '',
  file: null as File | null
})

const errors = reactive({
  name: '',
  description: '',
  file: ''
})

const isLoading = ref(false)
const isDragOver = ref(false)
const selectedFile = ref<File | null>(null)
const fileInput = ref<HTMLInputElement | null>(null)
const hasChanges = ref(false)
const isDownloading = ref(false)
const downloadId = ref<string | null>(null)
const initialData = reactive({
  name: '',
  description: ''
})

// Auth and toast composables
const authStore = useAuthStore()
const toast = useToastService()

// Computed properties
const moduleId = computed(() => props.module?.module_id)
const currentFileUrl = computed(() => props.module?.material_filepath)
const currentFileName = computed(() => {
  if (!currentFileUrl.value) return ''
  const url = currentFileUrl.value
  return url.substring(url.lastIndexOf('/') + 1)
})
const fileType = computed(() => props.module?.file_type || '')
const statusText = computed(() => (props.module?.status === 1 ? 'Active' : 'Inactive'))
const statusClass = computed(() =>
  props.module?.status === 1
    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
)
const isFormValid = computed(() => formData.name.trim().length > 0)
const nameValidationMessage = computed(() => {
  if (!formData.name.trim()) return 'Module name is required'
  if (formData.name.trim().length < 3) return 'Module name must be at least 3 characters'
  return ''
})
const isAdmin = computed(() => authStore.isAdmin)

const replaceConfirmation = ref('')
// New computed properties for replace confirmation
const needsReplaceConfirmation = computed(() =>
  !!currentFileUrl.value && !!selectedFile.value
)

const isReplaceConfirmed = computed(() =>
  !needsReplaceConfirmation.value || replaceConfirmation.value.trim().toUpperCase() === 'REPLACE'
)

// Update isFormValid to include replace confirmation
const canUpdate = computed(() =>
  isFormValid.value && hasChanges.value && isReplaceConfirmed.value
)

// Methods
const closeModal = () => {
  // Cancel any pending downloads
  if (downloadId.value) {
    downloadId.value = null
  }
  
  // Reset download state
  isDownloading.value = false
  
  emit('close')
}

const checkForChanges = () => {
  // Check if name or description has changed
  const nameChanged = formData.name !== initialData.name
  const descriptionChanged = formData.description !== initialData.description
  const fileChanged = selectedFile.value !== null

  hasChanges.value = nameChanged || descriptionChanged || fileChanged
}

const triggerFileInput = () => {
  fileInput.value?.click()
}

const onFileSelect = async (event: Event) => {
  const input = event.target as HTMLInputElement
  if (input.files?.length) {
    const file = input.files[0]
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
    if (!allowedTypes.includes(file.type)) {
      errors.file = 'Please select a PDF, DOC, or DOCX file'
      // Reset the input
      if (fileInput.value) {
        fileInput.value.value = ''
      }
      return
    }
    
    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      errors.file = 'File size must be less than 10MB'
      // Reset the input
      if (fileInput.value) {
        fileInput.value.value = ''
      }
      return
    }
    
    // Clear any previous file errors
    errors.file = ''
    
    // If there is a current file, show confirmation input instead of window.confirm
    if (currentFileUrl.value) {
      selectedFile.value = file
      replaceConfirmation.value = ''
      checkForChanges()
      return
    }
    selectedFile.value = file
    checkForChanges()
  }
}

const onFileDrop = async (event: DragEvent) => {
  event.preventDefault()
  isDragOver.value = false

  if (event.dataTransfer?.files.length) {
    const file = event.dataTransfer.files[0]
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
    if (!allowedTypes.includes(file.type)) {
      errors.file = 'Please select a PDF, DOC, or DOCX file'
      return
    }
    
    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      errors.file = 'File size must be less than 10MB'
      return
    }
    
    // Clear any previous file errors
    errors.file = ''
    
    // If there is a current file, show confirmation input instead of window.confirm
    if (currentFileUrl.value) {
      selectedFile.value = file
      replaceConfirmation.value = ''
      checkForChanges()
      return
    }
    selectedFile.value = file
    checkForChanges()
  }
}

const removeFile = () => {
  selectedFile.value = null
  replaceConfirmation.value = ''
  errors.file = ''
  if (fileInput.value) {
    fileInput.value.value = ''
  }
  checkForChanges()
}

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const handleSubmit = async () => {
  if (!isFormValid.value || !moduleId.value || !hasChanges.value) return

  isLoading.value = true
  try {
    // Update the module details
    const updatePayload = {
      name: formData.name,
      description: formData.description
    }

    const updateResponse = await ModulesService.updateModule(moduleId.value, updatePayload)

    // If there's a new file to upload, handle it
    if (selectedFile.value) {
      await ModulesService.uploadModuleFile(moduleId.value, selectedFile.value)
    }

    // Emit success with module ID so parent can refresh the specific module
    emit('success', { 
      ...updateResponse.data, 
      hasFileUpdate: !!selectedFile.value 
    })
    closeModal()
  } catch (error: any) {
    console.error('Error updating module:', error)
    errors.name = error.message || 'Failed to update module. Please try again.'
  } finally {
    isLoading.value = false
  }
}

// Drag and drop handlers
const onDragOver = (event: DragEvent) => {
  event.preventDefault()
  isDragOver.value = true
}

const onDragEnter = (event: DragEvent) => {
  event.preventDefault()
  isDragOver.value = true
}

const onDragLeave = () => {
  isDragOver.value = false
}

// Download file function
const downloadFile = async () => {
  if (!currentFileUrl.value || !moduleId.value) {
    toast.error('No file available', 'This module does not have a file attached.')
    return
  }

  if (!isAdmin.value) {
    toast.error('Access Denied', 'Only administrators can download module materials.')
    return
  }

  // Prevent multiple simultaneous downloads
  if (isDownloading.value) {
    toast.warning('Download in Progress', 'Please wait for the current download to complete.')
    return
  }

  isDownloading.value = true
  
  // Generate unique download ID for this session
  const currentDownloadId = `download_${moduleId.value}_${Date.now()}`
  downloadId.value = currentDownloadId
  
  try {
    const blob = await ModulesService.downloadFile(moduleId.value)
    
    // Check if this download is still current (user hasn't cancelled)
    if (downloadId.value !== currentDownloadId) {
      return
    }
    
    // Create download link with cache-busting
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    
    // Generate filename with timestamp to avoid cache conflicts
    const originalFileName = currentFileName.value || `module_${moduleId.value}.pdf`
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)
    const fileExtension = originalFileName.split('.').pop()
    const baseName = originalFileName.replace(/\.[^/.]+$/, '')
    const fileName = `${baseName}_${timestamp}.${fileExtension}`
    
    link.download = fileName
    
    // Add cache-busting attributes
    link.setAttribute('download', fileName)
    link.style.display = 'none'
    
    // Trigger download
    document.body.appendChild(link)
    link.click()
    
    // Cleanup with slight delay to ensure download starts
    setTimeout(() => {
      if (document.body.contains(link)) {
        document.body.removeChild(link)
      }
      window.URL.revokeObjectURL(url)
    }, 100)
    
    toast.success('Download Started', `Downloading ${originalFileName}`)
  } catch (error: any) {
    console.error('Download failed:', error)
    toast.error('Download Failed', getErrorMessage(error, 'Failed to download the file.'))
  } finally {
    isDownloading.value = false
    downloadId.value = null
  }
}

// Reset form function
const resetForm = () => {
  formData.name = ''
  formData.description = ''
  formData.file = null
  selectedFile.value = null
  isDragOver.value = false
  hasChanges.value = false
  errors.name = ''
  errors.description = ''
  errors.file = ''

  // Clear file input
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

// Initialize form data
const initializeForm = () => {
  if (props.module) {
    formData.name = props.module.name || ''
    formData.description = props.module.description || ''

    // Store initial values for change detection
    initialData.name = props.module.name || ''
    initialData.description = props.module.description || ''

    // Reset changes flag
    hasChanges.value = false
    selectedFile.value = null
  }
}

// Reset form when modal closes or initialize when it opens
watch(
  () => props.isVisible,
  isVisible => {
    if (!isVisible) {
      // Reset form data when modal closes
      resetForm()
    } else if (isVisible && props.module) {
      // Initialize form when modal becomes visible
      nextTick(() => {
        initializeForm()
      })
    }
  }
)

// Initialize form when module prop changes
watch(
  () => props.module,
  () => {
    if (props.isVisible && props.module) {
      // Use nextTick to ensure DOM is updated
      nextTick(() => {
        initializeForm()
      })
    }
  },
  { immediate: true }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BaseModal, {
    "is-visible": _ctx.isVisible,
    title: "Edit Module",
    subtitle: "Updating module to the system",
    icon: _unref(Edit),
    size: "2xl",
    "show-footer": true,
    onClose: closeModal
  }, {
    footer: _withCtx(() => [
      _createVNode(BaseButton, {
        text: "Cancel",
        variant: "secondary",
        disabled: isLoading.value,
        onClick: closeModal
      }, null, 8, ["disabled"]),
      _createVNode(BaseButton, {
        text: isLoading.value ? 'Updating...' : 'Update Module',
        variant: "primary",
        loading: isLoading.value,
        disabled: isLoading.value || !canUpdate.value,
        onClick: handleSubmit
      }, null, 8, ["text", "loading", "disabled"])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("form", {
          onSubmit: _withModifiers(handleSubmit, ["prevent"]),
          class: "space-y-6"
        }, [
          _createElementVNode("div", _hoisted_2, [
            _cache[3] || (_cache[3] = _createElementVNode("label", {
              for: "module-name",
              class: "block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
            }, [
              _createTextVNode(" Module Name "),
              _createElementVNode("span", { class: "text-red-500" }, "*")
            ], -1)),
            _withDirectives(_createElementVNode("input", {
              id: "module-name",
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formData.name) = $event)),
              type: "text",
              class: _normalizeClass(["block w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400", {
              'border-red-500 focus:border-red-500 focus:ring-red-500/20': errors.name
            }]),
              placeholder: "Enter module name",
              required: "",
              onInput: checkForChanges
            }, null, 34), [
              [_vModelText, formData.name]
            ]),
            (errors.name)
              ? (_openBlock(), _createElementBlock("p", _hoisted_3, _toDisplayString(errors.name), 1))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("div", _hoisted_4, [
            _cache[4] || (_cache[4] = _createElementVNode("label", {
              for: "module-description",
              class: "block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
            }, " Description ", -1)),
            _withDirectives(_createElementVNode("textarea", {
              id: "module-description",
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formData.description) = $event)),
              rows: "4",
              class: _normalizeClass(["block w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 resize-none", {
              'border-red-500 focus:border-red-500 focus:ring-red-500/20':
                errors.description
            }]),
              placeholder: "Enter module description (optional)",
              onInput: checkForChanges
            }, null, 34), [
              [_vModelText, formData.description]
            ]),
            (errors.description)
              ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(errors.description), 1))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("div", _hoisted_7, [
              _cache[5] || (_cache[5] = _createElementVNode("label", { class: "block text-sm font-semibold text-gray-700 dark:text-gray-300" }, " Module Status ", -1)),
              _createElementVNode("span", {
                class: _normalizeClass(["inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold shadow-sm", statusClass.value])
              }, _toDisplayString(statusText.value), 3)
            ])
          ]),
          (currentFileUrl.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                _cache[7] || (_cache[7] = _createElementVNode("label", { class: "block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2" }, " Current File ", -1)),
                _createElementVNode("div", _hoisted_9, [
                  _createElementVNode("div", _hoisted_10, [
                    _createVNode(_unref(FileText), { class: "h-8 w-8 text-blue-500 mr-3 flex-shrink-0" }),
                    _createElementVNode("div", _hoisted_11, [
                      _createElementVNode("p", _hoisted_12, _toDisplayString(currentFileName.value), 1),
                      _createElementVNode("p", _hoisted_13, _toDisplayString(fileType.value?.toUpperCase() || 'Unknown type'), 1)
                    ])
                  ]),
                  (isAdmin.value)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        type: "button",
                        onClick: downloadFile,
                        disabled: isDownloading.value,
                        class: "text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors duration-200 ml-2 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0",
                        title: "Download file (Admin only)"
                      }, [
                        (isDownloading.value)
                          ? (_openBlock(), _createBlock(_unref(Loader2), {
                              key: 0,
                              class: "h-5 w-5 animate-spin"
                            }))
                          : (_openBlock(), _createBlock(_unref(Download), {
                              key: 1,
                              class: "h-5 w-5"
                            }))
                      ], 8, _hoisted_14))
                    : (currentFileUrl.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                          _createVNode(_unref(Download), { class: "h-5 w-5" })
                        ]))
                      : _createCommentVNode("", true)
                ]),
                (currentFileUrl.value && !isAdmin.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                      _createVNode(_unref(Info), { class: "w-4 h-4 mr-2" }),
                      _cache[6] || (_cache[6] = _createTextVNode(" Download restricted to administrators ", -1))
                    ]))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_17, [
            _createElementVNode("label", _hoisted_18, _toDisplayString(currentFileUrl.value ? 'Replace File' : 'Module File'), 1),
            _createElementVNode("div", _hoisted_19, [
              _createElementVNode("div", {
                class: _normalizeClass(["relative border-2 border-dashed rounded-xl p-6 transition-colors duration-200 cursor-pointer", [
                isDragOver.value
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500'
              ]]),
                onDrop: onFileDrop,
                onDragover: _withModifiers(onDragOver, ["prevent"]),
                onDragenter: _withModifiers(onDragEnter, ["prevent"]),
                onDragleave: _withModifiers(onDragLeave, ["prevent"]),
                onClick: triggerFileInput
              }, [
                _createElementVNode("input", {
                  ref_key: "fileInput",
                  ref: fileInput,
                  type: "file",
                  accept: ".pdf,.doc,.docx",
                  class: "hidden",
                  onChange: onFileSelect
                }, null, 544),
                (!selectedFile.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                      (isDragOver.value)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                            _createVNode(_unref(Upload), { class: "mx-auto h-12 w-12 text-blue-500 mb-4" }),
                            _cache[8] || (_cache[8] = _createElementVNode("div", { class: "text-sm font-medium text-blue-600 dark:text-blue-400" }, " Release to drop your file ", -1)),
                            _cache[9] || (_cache[9] = _createElementVNode("p", { class: "text-xs text-blue-500/80 dark:text-blue-400/80 mt-1" }, " PDF, DOC, DOCX up to 10MB ", -1))
                          ], 64))
                        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                            _createVNode(_unref(Upload), { class: "mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" }),
                            _cache[10] || (_cache[10] = _createElementVNode("div", { class: "text-sm text-gray-600 dark:text-gray-300" }, [
                              _createElementVNode("span", { class: "font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 cursor-pointer" }, " Click to upload "),
                              _createTextVNode(" or drag and drop ")
                            ], -1)),
                            _cache[11] || (_cache[11] = _createElementVNode("p", { class: "text-xs text-gray-500 mt-2" }, "PDF, DOC, DOCX up to 10MB", -1))
                          ], 64))
                    ]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_21, [
                      _createElementVNode("div", _hoisted_22, [
                        _createVNode(_unref(FileText), { class: "h-8 w-8 text-blue-500 mr-3 flex-shrink-0" }),
                        _createElementVNode("div", _hoisted_23, [
                          _createElementVNode("p", _hoisted_24, _toDisplayString(selectedFile.value.name), 1),
                          _createElementVNode("p", _hoisted_25, _toDisplayString(formatFileSize(selectedFile.value.size)), 1)
                        ])
                      ]),
                      _createElementVNode("button", {
                        type: "button",
                        class: "text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors duration-200 flex-shrink-0",
                        onClick: _withModifiers(removeFile, ["stop"]),
                        title: "Remove file"
                      }, [
                        _createVNode(_unref(X), { class: "h-5 w-5" })
                      ])
                    ]))
              ], 34),
              (errors.file)
                ? (_openBlock(), _createElementBlock("p", _hoisted_26, _toDisplayString(errors.file), 1))
                : _createCommentVNode("", true)
            ])
          ]),
          (needsReplaceConfirmation.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_27, [
                _cache[12] || (_cache[12] = _createElementVNode("div", { class: "font-semibold text-yellow-800 mb-2" }, "Confirmation Required", -1)),
                _cache[13] || (_cache[13] = _createElementVNode("div", { class: "text-sm text-yellow-700 mb-2" }, [
                  _createTextVNode(" Type "),
                  _createElementVNode("span", { class: "font-bold" }, "REPLACE"),
                  _createTextVNode(" to confirm replacing the current file: ")
                ], -1)),
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((replaceConfirmation).value = $event)),
                  type: "text",
                  class: "w-full px-3 py-2 border border-yellow-300 rounded text-sm",
                  placeholder: "Type REPLACE to confirm",
                  autocomplete: "off"
                }, null, 512), [
                  [_vModelText, replaceConfirmation.value]
                ])
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_28, [
            (nameValidationMessage.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_29, [
                  _createVNode(_unref(XCircle), { class: "w-4 h-4 mr-2 flex-shrink-0" }),
                  _createTextVNode(" " + _toDisplayString(nameValidationMessage.value), 1)
                ]))
              : (isFormValid.value && hasChanges.value && needsReplaceConfirmation.value && !isReplaceConfirmed.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_30, [
                    _createVNode(_unref(Info), { class: "w-4 h-4 mr-2 flex-shrink-0" }),
                    _cache[14] || (_cache[14] = _createTextVNode(" Please confirm module file replacement to proceed ", -1))
                  ]))
                : (isFormValid.value && !nameValidationMessage.value && hasChanges.value && isReplaceConfirmed.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_31, [
                      _createVNode(_unref(CheckCircle), { class: "w-4 h-4 mr-2 flex-shrink-0" }),
                      _cache[15] || (_cache[15] = _createTextVNode(" Ready to update module ", -1))
                    ]))
                  : (isFormValid.value && !nameValidationMessage.value && !hasChanges.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_32, [
                        _createVNode(_unref(Info), { class: "w-4 h-4 mr-2 flex-shrink-0" }),
                        _cache[16] || (_cache[16] = _createTextVNode(" Make changes to enable update ", -1))
                      ]))
                    : _createCommentVNode("", true)
          ])
        ], 32)
      ])
    ]),
    _: 1
  }, 8, ["is-visible", "icon"]))
}
}

})